import { React, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Container,
  Grid,
  styled,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { isNil, isNull, map } from 'lodash'
import PropTypes from 'prop-types'

import { DigitalCertificate } from '../api'
import axios from '../api/axios'
import { REACT_APP_ENV } from '../common/constants'
import AppSpinner from '../components/AppSpinner'
import DigitalCertificateAccesories from '../components/DigitalCertificate/DigitalCertificateAccesories'
import DigitalCertificateBodyCondition from '../components/DigitalCertificate/DigitalCertificateBodyCondition'
import DigitalCertificateCarBasicData from '../components/DigitalCertificate/DigitalCertificateCarBasicData'
import DigitalCertificateCarControl from '../components/DigitalCertificate/DigitalCertificateCarControl'
import DigitalCertificateCarGallery from '../components/DigitalCertificate/DigitalCertificateCarGallery'
import DigitalCertificateCarRating from '../components/DigitalCertificate/DigitalCertificateCarRating'
import DigitalCertificateHeader from '../components/DigitalCertificate/DigitalCertificateHeader'
import DigitalCertificateInteriorCondition from '../components/DigitalCertificate/DigitalCertificateInteriorCondition'
import DigitalCertificateTyres from '../components/DigitalCertificate/DigitalCertificateTyres'
import DigitalCertificateWorksTodo from '../components/DigitalCertificate/DigitalCertificateWorksTodo'
import Ratings from '../components/Ratings'
import Section from '../components/Section'
import SectionHeader from '../components/SectionHeader'
import TabPanel from '../components/TabPanel'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import {
  AccessoriesIcon,
  BodyIcon,
  CheckIcon,
  InteriorIcon,
  TiresIcon,
} from '../icons'
import { checkEnvironment } from '../utils/helpers'

const CERTIFICATE_TABS = [
  { id: 'nadwozie', name: 'Nadwozie', icon: <BodyIcon /> },
  { id: 'wnetrze', name: 'Wnętrze', icon: <InteriorIcon /> },
  { id: 'wyposazenie', name: 'Wyposażenie', icon: <AccessoriesIcon /> },
  { id: 'opony', name: 'Opony', icon: <TiresIcon /> },
  { id: 'stan', name: 'Stan techniczny', icon: <CheckIcon /> },
]

const StyledTitleBox = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  '& .MuiBox-root': {
    alignItems: 'center',
  },
})

function ControlsDigitalCertificate({ index }) {
  const { t } = useTranslation()
  const axiosPrivate = useAxiosPrivate()
  const { id: controlId } = useParams()
  const [selected, setSelected] = useState(0)
  const [certificateData, setCertificateData] = useState(null)
  const { certificateId } = document.getElementById('root').dataset || {}
  const reqCertificateId = index ? certificateId : controlId
  const navigate = useNavigate()

  if (isNil(reqCertificateId)) {
    navigate('*')
  }

  const handleMenuClick = (e, id) => {
    setSelected(id)
  }

  useEffect(() => {
    const provider = checkEnvironment(REACT_APP_ENV, 'usedcars')
      ? axios
      : axiosPrivate

    DigitalCertificate.getCertificateData(provider, reqCertificateId)
      .then(({ data }) => {
        setCertificateData(data)
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e))
  }, [axiosPrivate, reqCertificateId])

  return isNull(certificateData) ? (
    <AppSpinner />
  ) : (
    <Container
      maxWidth="xl"
      sx={{ px: { xs: index ? 0 : 2, sm: index ? 0 : 3 } }}
    >
      {!index && <DigitalCertificateHeader certificateData={certificateData} />}
      <Section>
        <DigitalCertificateCarRating
          certificateData={certificateData}
          index={index}
        />
      </Section>
      <Section>
        <SectionHeader title={t('summarySectionTitle')} small />
        <DigitalCertificateCarBasicData certificateData={certificateData} />
      </Section>
      <Section>
        <Tabs
          value={selected}
          onChange={handleMenuClick}
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
          sx={({ palette }) => ({
            backgroundColor: palette.common.white,
            '& .MuiTabs-indicator': {
              top: 0,
              bottom: 'auto',
              height: 4,
              backgroundColor: palette.common.brand,
            },
            '.MuiTabs-flexContainer': {
              justifyContent: 'space-between',
            },
            '.MuiTabs-scrollButtons.Mui-disabled': {
              opacity: 0.25,
            },
          })}
        >
          {map(CERTIFICATE_TABS, (tab, key) => (
            <Tab
              key={key}
              icon={tab.icon}
              label={tab.name}
              disableRipple
              disableFocusRipple
              sx={({ palette, spacing, typography }) => ({
                p: `${spacing(1)} ${spacing(2)}`,
                color: palette.text.primary,
                backgroundColor: palette.background.paper,
                textTransform: 'none',
                fontSize: typography.body2.fontSize,
                fontWeight: 400,
                minWidth: 120,
                maxWidth: 150,
                width: '20%',
                '&.Mui-selected': {
                  fontWeight: 700,
                },
              })}
            />
          ))}
        </Tabs>
        <TabPanel value={selected} index={0} sx={{ pt: 5, pb: 10 }}>
          <StyledTitleBox>
            <Ratings
              big
              label="Ocena nadwozia"
              orientation="vertical"
              score={certificateData.rating?.body}
              starSize={36}
            />
          </StyledTitleBox>
          <DigitalCertificateBodyCondition
            certificateData={certificateData}
            index={index}
          />
        </TabPanel>
        <TabPanel value={selected} index={1} sx={{ pt: 5, pb: 10 }}>
          <StyledTitleBox>
            <Ratings
              big
              label="Ocena wnętrza"
              orientation="vertical"
              score={certificateData.rating?.interior}
              starSize={36}
            />
          </StyledTitleBox>
          <DigitalCertificateInteriorCondition
            certificateData={certificateData}
            index={index}
          />
        </TabPanel>
        <TabPanel value={selected} index={2} sx={{ pt: 5, pb: 10 }}>
          <StyledTitleBox>
            <Ratings
              big
              label="Ocena kompletności wyposażenia"
              orientation="vertical"
              score={certificateData.rating?.equipment}
              starSize={36}
            />
          </StyledTitleBox>
          <DigitalCertificateAccesories certificateData={certificateData} />
        </TabPanel>
        <TabPanel value={selected} index={3} sx={{ pt: 5, pb: 10 }}>
          <StyledTitleBox>
            <Ratings
              big
              label="Ocena stanu opon"
              orientation="vertical"
              score={certificateData.rating?.tyres}
              starSize={36}
            />
          </StyledTitleBox>
          <DigitalCertificateTyres certificateData={certificateData} />
        </TabPanel>
        <TabPanel value={selected} index={4} sx={{ pt: 5, pb: 10 }}>
          <StyledTitleBox>
            <Ratings
              big
              label="Ocena stanu technicznego"
              orientation="vertical"
              score={certificateData.rating?.technical}
              starSize={36}
            />
          </StyledTitleBox>
          <Box sx={{ mt: 5 }}>
            <Grid container spacing={5}>
              <Grid item xs={12} md={6}>
                <Typography variant="h3" sx={{ mb: 2 }}>
                  {t('controlsEditTodo')}
                </Typography>
                <DigitalCertificateWorksTodo
                  certificateData={certificateData}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DigitalCertificateCarGallery
                  certificateData={certificateData}
                />
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ mt: 5 }}>
            <DigitalCertificateCarControl certificateData={certificateData} />
          </Box>
        </TabPanel>
      </Section>
    </Container>
  )
}

ControlsDigitalCertificate.propTypes = {
  index: PropTypes.bool,
}

ControlsDigitalCertificate.defaultProps = {
  index: false,
}

export default ControlsDigitalCertificate
